import React from 'react';
import { Block, ImageBlockContent, ObjectFit } from '../types';
import { UserRegularIcon, NeueImageFillRegularIcon, NeueImageFitRegularIcon, RefreshLightIcon } from 'src/monet/icons';
import { DalleImageTiles } from './dalle-image-tiles';
import { useImageBlockOperations } from '../helpers/block/use-image-block-operations';
import { ControlPanelActionButton } from './control-panel/action-button';
import { ControlPanelToggleButton } from './control-panel/toggle-button';
import { DalleForm } from './add-image-control-panel-content';
import { useEditorStore } from '../editor-store';
import { ToggleBlockCaption } from './toggle-block-caption';
import { ToggleBackground } from './toggle-background';
import { TogglePersonalContent } from './toggle-personal-content';

type Props = {
  block: Block;
  onChangeObjectFit: (objectFit: ObjectFit) => void;
  onReplaceBlock: () => void;
};

const ObjectFitPreferences = ({
  objectFit,
  onFitClick,
  onFillClick,
}: {
  objectFit: ObjectFit;
  onFitClick: () => void;
  onFillClick: () => void;
}) => (
  <div className='flex gap-2 text-neue-journey-fg'>
    <ControlPanelToggleButton
      label='Fit'
      icon={<NeueImageFitRegularIcon className='transform-gpu' />}
      selected={objectFit === 'contain'}
      onClick={onFitClick}
    />
    <ControlPanelToggleButton
      label='Fill'
      icon={<NeueImageFillRegularIcon className='transform-gpu' />}
      selected={objectFit === 'cover'}
      onClick={onFillClick}
    />
  </div>
);

export const EditImageControlPanelContent = ({ block, onChangeObjectFit, onReplaceBlock }: Props) => {
  const { onNewImageUrl } = useImageBlockOperations(block);

  const selectBlock = useEditorStore((state) => state.selectBlock);
  const { searchDalle, loading: dalleLoading } = useImageBlockOperations(block);

  const onDalleSubmit = async (prompt: string, service: string) => {
    searchDalle(block, prompt, service);
    selectBlock(block.id);
  };

  const { url: selectedDalleUrl, objectFit, dalleParams, type } = block.content as ImageBlockContent;

  const contentObject = {
    file_url: selectedDalleUrl,
    file_content_type: type,
    dalle_params: dalleParams,
    name:
      (block.content as ImageBlockContent)?.file?.name ||
      (block.content as ImageBlockContent)?.contentObject?.name ||
      'untitled',
  };

  if (dalleParams && dalleParams.urls) {
    return (
      <>
        <div className='flex flex-col gap-4'>
          <DalleForm block={block} onDalleSubmit={onDalleSubmit} />
          <DalleImageTiles
            selectedDalleUrl={selectedDalleUrl || ''}
            onSelectDalleImage={(url) => {
              onNewImageUrl({
                url,
                dalleParams: {
                  urls: dalleParams.urls,
                  prompt: dalleParams.prompt,
                },
                contentUUID: (block.content as ImageBlockContent)?.contentUUID || '',
              });
            }}
            imageTiles={dalleParams.urls.map((url) => ({
              show: true,
              url: url,
            }))}
            dalleLoading={dalleLoading}
          />
          <ObjectFitPreferences
            objectFit={objectFit}
            onFitClick={() => {
              onChangeObjectFit('contain');
            }}
            onFillClick={() => {
              onChangeObjectFit('cover');
            }}
          />
          <ToggleBlockCaption block={block} />
          <ToggleBackground block={block} />
          <TogglePersonalContent
            block={block}
            icon={<UserRegularIcon className='shrink-0' />}
            contentObject={contentObject}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className='flex flex-col gap-4'>
        <ObjectFitPreferences
          objectFit={objectFit}
          onFitClick={() => {
            onChangeObjectFit('contain');
          }}
          onFillClick={() => {
            onChangeObjectFit('cover');
          }}
        />
        <div className='flex flex-col'>
          <ToggleBlockCaption block={block} />
          <ToggleBackground block={block} />
          <TogglePersonalContent
            block={block}
            icon={<UserRegularIcon className='shrink-0' />}
            contentObject={contentObject}
          />
          <ControlPanelActionButton label='Replace image' iconComponent={RefreshLightIcon} onClick={onReplaceBlock} />
        </div>
      </div>
    </>
  );
};
